import { Dialog } from "@headlessui/react";
import { IconSearch, IconX } from "@tabler/icons-react";
import Button from "../Button";
import InputText from "../InputText";
import { useForm } from "react-hook-form";
import DatePicker from "../DatePicker";
import PhoneNumberField from "../PhoneNumberField";
import EmailField from "../EmailField";
import useToggle from "../../hooks/useToggle";
import React, { useState } from "react";
import { convertToCRMDate } from "../../helper";
import SelectField from "../SelectField";

interface Field {
  label: string;
  name: string;
  defaultValue?: string | { value: string; label: string };
  type: "text" | "date" | "email" | "phone" | "select";
  options?: { label: string; value: string }[];
}

export interface SearchModalProps {
  searchFields: Field[];
  onSearchSubmit: (values: Record<string, string>, per_page: number) => void;
  onClearFilter?: (perPage: number) => void;
  perPage?: number;
}

function SearchModal({
  searchFields,
  onSearchSubmit,
  onClearFilter,
  perPage = 20,
}: SearchModalProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
  });
  const { value: isOpen, setTrue: open, setFalse: close } = useToggle(false);
  const [searchFieldValues, setSearchFieldValues] = useState("");

  const onSubmit = (values) => {
    const formattedSearchPayload = Object.keys(values).reduce(
      (payload, fieldKey) => {
        let fieldValue = values[fieldKey];
        if (fieldValue) {
          const fieldLabel = searchFields.find(
            ({ name }) => name === fieldKey
          )?.label;
          if (fieldKey.includes("date")) {
            fieldValue = convertToCRMDate(fieldValue);
          }
          if (typeof fieldValue === "object" && fieldValue?.label) {
            payload.formattedString += `${fieldLabel}: ${fieldValue.label} `;
            payload.filteredValues[fieldKey] = fieldValue.value;
          } else {
            payload.formattedString += `${fieldLabel}: ${fieldValue} `;
            payload.filteredValues[fieldKey] = fieldValue;
          }
        }
        return payload;
      },
      { formattedString: "", filteredValues: {} }
    );
    setSearchFieldValues(formattedSearchPayload.formattedString);
    if (Object.keys(formattedSearchPayload.filteredValues).length > 0) {
      onSearchSubmit(formattedSearchPayload.filteredValues, perPage);
    }
    close();
  };

  const handleClearFilter = () => {
    if (searchFieldValues.length > 0) {
      onClearFilter(perPage);
    }
    setSearchFieldValues("");
    reset(
      searchFields.reduce((acc, field) => ({ ...acc, [field.name]: "" }), {})
    );
    close();
  };

  return (
    <>
      <div
        className={`w-10 ${
          searchFieldValues.length > 0 ? " flex-1" : ""
        } h-10 text-base rounded-[10px] border border-gray-300 text-black bg-white flex items-center`}
      >
        <button className="p-2 h-full" onClick={open}>
          <IconSearch className="text-gray-500" size={20} />
        </button>
        {searchFieldValues.length > 0 && (
          <>
            <input
              type="text"
              value={searchFieldValues}
              className="text-gray-500 outline-none cursor-pointer bg-white text-sm flex-1 border-x border-gray-400 px-2"
              readOnly
              onClick={open}
            />
            <button
              className="p-2 h-full flex items-center gap-0.5 text-gray-500"
              onClick={handleClearFilter}
            >
              <IconX className="text-gray-500" size={14} />
              <p className=" text-sm underline underline-offset-2">Clear</p>
            </button>
          </>
        )}
      </div>
      <Dialog
        open={isOpen}
        as="div"
        className="relative z-10 focus:outline-none"
        onClose={close}
      >
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="w-full rounded-xl bg-white relative max-w-[540px]">
              <div className="flex items-center justify-between border-b border-gray-200 gap-3 p-4 text-gray-800">
                <h2 className="text-lg font-medium ">Enter Data for Search</h2>
                <button onClick={close}>
                  <IconX />
                </button>
              </div>
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid gap-3 p-4">
                  {searchFields.map((field) => {
                    if (field.type === "text") {
                      return (
                        <InputText
                          control={control}
                          key={field.name}
                          name={field?.name}
                          label={field.label}
                          type="text"
                          className="min-[460px]:flex grid items-center"
                          labelClassName="min-w-[150px]"
                        />
                      );
                    } else if (field.type === "email") {
                      return (
                        <EmailField
                          control={control}
                          key={field.name}
                          name={field?.name}
                          label={field.label}
                          wrapperClassName="min-[460px]:flex grid items-center"
                          labelClassName="min-w-[150px]"
                          showError={true}
                        />
                      );
                    } else if (field.type === "phone") {
                      return (
                        <PhoneNumberField
                          control={control}
                          key={field.name}
                          name={field?.name}
                          label={field.label}
                          wrapperClassName="min-[460px]:flex grid items-center"
                          labelClassName="min-w-[150px]"
                          showError={false}
                        />
                      );
                    } else if (field.type === "date") {
                      return (
                        <DatePicker
                          control={control}
                          key={field.name}
                          name={field.name}
                          label={field.label}
                          wrapperClassName="min-[460px]:flex grid items-center"
                          labelClassName="min-w-[150px]"
                        />
                      );
                    } else if (field.type === "select") {
                      return (
                        <SelectField
                          control={control as any}
                          key={field.name}
                          name={field.name}
                          label={field.label}
                          defaultValue={field.defaultValue as any}
                          options={field.options || []}
                          wrapperClassName="min-[460px]:flex grid items-center"
                          labelClassName="min-w-[150px]"
                        />
                      );
                    }
                    return null;
                  })}
                </div>
                <div className="flex gap-4 p-4">
                  <Button
                    btnClassName="w-full"
                    onClick={handleClearFilter}
                    variant="gray"
                    type="button"
                  >
                    Clear
                  </Button>
                  <Button
                    btnClassName="w-full"
                    type="submit"
                    variant="blue"
                    disabled={!isValid}
                  >
                    Search
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default SearchModal;
